import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { GetFinanceLedgerAccountsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LedgerAccountCreationForm,
  LedgerAccountFormValues,
  formValidation,
} from 'modules/finance/components/Accounting/NewLedgerAccountForm'
import Loading from 'modules/finance/components/Loading'
import { useUpdateLedgerAccount } from 'modules/finance/hooks/accounting/useUpdateLedgerAccount'

interface EditLedgerAccountModalProps {
  id: number
  onClose: () => void
}

const EditLedgerAccountModal: React.FC<EditLedgerAccountModalProps> = ({ id, onClose }) => {
  const { updateLedgerAccount, loading: updating } = useUpdateLedgerAccount()

  const { data, loading: loadingData } = useQuery(GetFinanceLedgerAccountsDocument, {
    variables: { ids: [id] },
  })

  const ledgerAccount = data?.finance.accounts[0]
  const legalEntity = ledgerAccount?.legalEntity

  const form = useForm<LedgerAccountFormValues>({
    enableReinitialize: true,
    defaultValues: {
      number: ledgerAccount?.number || '',
      name: ledgerAccount?.name || '',
      isDisabled: false,
      externalId: ledgerAccount?.externalId || '',
      type: ledgerAccount?.type || 'bank',
      legalEntityId: ledgerAccount?.legalEntityId || 0,
    },
    validateOnBlur: true,
    validate: formValidation,
    onSubmit: async (values) => {
      const result = await updateLedgerAccount({
        number: values.number,
        name: values.name,
        externalId: values.externalId,
        type: values.type,
        id,
      })

      const handled = handleResult(result, (errors) => {
        form.setErrors(errors)
        app.setFlash({
          type: 'wrong',
          message: errors.errors || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.chart_of_accounts.edit.success'),
        })
        onClose()
      }
      return handled
    },
  })

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.chart_of_accounts.edit.title')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('finance.buttons.cancel')}
          </Button>
          <Button submit disabled={!form.isValid || !form.dirty || loadingData || updating}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Loading loading={!ledgerAccount || loadingData}>
        {legalEntity && <LedgerAccountCreationForm form={form} legalEntities={[legalEntity]} />}
      </Loading>
    </Modal>
  )
}

export default observer(EditLedgerAccountModal)
